import styled from '@/styles';
import { DependabilityV2, TrendStat } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { categoryColor } from './tierColors';

const Grid = styled('div', { display: 'flex', gap: '12px', flexWrap: 'wrap' });
const StatCard = styled('div', {
  flex: '1 1 120px',
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: dependabilityV2Colors.depSurface,
  border: `1px solid ${dependabilityV2Colors.depBorder}`,
  minWidth: '120px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});
const StatValue = styled('div', {
  fontSize: '$lg',
  fontWeight: 700,
  color: dependabilityV2Colors.depInk,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});
const StatLabel = styled('div', {
  fontSize: '$md',
  color: dependabilityV2Colors.depInk,
});

// heroicons-mini arrow-long-up, per the Figma export (37920:11833); the down
// variant is the same path flipped. Only the up arrow appears in the frames —
// its green is verified; the down red follows the negative category color.
const ArrowLongIcon = ({
  direction,
  color,
  testId,
}: {
  direction: 'up' | 'down';
  color: string;
  testId: string;
}) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    data-testid={testId}
    aria-hidden="true"
    style={direction === 'down' ? { transform: 'rotate(180deg)' } : undefined}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 16C9.58579 16 9.25 15.6642 9.25 15.25L9.25 6.6599L7.2996 8.76034C7.01775 9.06387 6.5432 9.08145 6.23966 8.7996C5.93613 8.51774 5.91856 8.0432 6.20041 7.73966L9.45041 4.23966C9.59232 4.08684 9.79145 4 10 4C10.2086 4 10.4077 4.08684 10.5496 4.23966L13.7996 7.73966C14.0815 8.04319 14.0639 8.51774 13.7603 8.79959C13.4568 9.08145 12.9823 9.06387 12.7004 8.76034L10.75 6.6599L10.75 15.25C10.75 15.6642 10.4142 16 10 16Z"
      fill={color}
    />
  </svg>
);

const TrendArrow = ({ stat, id }: { stat: TrendStat; id: string }) => {
  if (stat.trend === 'up')
    return (
      <ArrowLongIcon
        direction="up"
        testId={`trend-up-${id}`}
        color={dependabilityV2Colors.depFeedPositive}
      />
    );
  if (stat.trend === 'down')
    return (
      <ArrowLongIcon
        direction="down"
        testId={`trend-down-${id}`}
        color={categoryColor('negative')}
      />
    );
  return null;
};

// The payload carries unrounded floats — a computed on-time rate arrives as
// 83.33333333333333 and rendered literally, overflowing the tile. Capped at
// ONE decimal rather than whole numbers: a rate that really is 96.2% should
// still read 96.2%, so this fixes the overflow without discarding precision
// the backend meant to convey. `Number()` drops a trailing ".0" so a clean
// 100 stays "100%", not "100.0%".
const formatRate = (value: number): string => `${Number(value.toFixed(1))}%`;
// Ratings are 1-5 and always read to one decimal, matching WorkerRowV2:343.
const formatRating = (value: number): string => value.toFixed(1);

interface Props {
  stats: DependabilityV2['stats'];
}

const StatCards = ({ stats }: Props) => (
  <Grid>
    <StatCard>
      <StatLabel>Shifts completed</StatLabel>
      <StatValue data-testid="stat-shifts_completed">
        {stats.shifts_completed}
      </StatValue>
    </StatCard>
    <StatCard>
      <StatLabel>On-time rate</StatLabel>
      <StatValue data-testid="stat-on_time_rate">
        {stats.on_time_rate.current === null
          ? '—'
          : formatRate(stats.on_time_rate.current)}
        <TrendArrow stat={stats.on_time_rate} id="on_time_rate" />
      </StatValue>
    </StatCard>
    <StatCard>
      <StatLabel>Client rating</StatLabel>
      <StatValue data-testid="stat-client_rating">
        {stats.client_rating.current === null
          ? '—'
          : formatRating(stats.client_rating.current)}
        <TrendArrow stat={stats.client_rating} id="client_rating" />
      </StatValue>
    </StatCard>
  </Grid>
);

export default StatCards;
